import { useState } from "react";
import { Layers, Pencil, Settings2 } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  NO_GROUP_LABEL,
  groupErrorMessage,
  useAgreementGroups,
  useSetAgreementGroups,
  type AgreementGroupBrief,
} from "@/features/contractors/agreementGroups";
import { AgreementGroupsManagerModal } from "./AgreementGroupsManagerModal";

/**
 * „Grupy umowy” na karcie umowy ramowej.
 *
 * Każdy pracownik widzi, w jakich grupach umowa jest (albo „Bez przypisanej
 * grupy”). Osoby z uprawnieniem „Grupy umów ramowych” (canManageAgreementGroups)
 * przypinają i odpinają ją z listy grup oraz zakładają nowe grupy. Zapis idzie
 * jednym PUT-em z PEŁNYM zbiorem — zaznaczenia działają od razu, bez „Zapisz”.
 */
export function AgreementGroupsSection({
  agreementId,
  groups,
  canManage,
}: {
  agreementId: string;
  groups: AgreementGroupBrief[];
  canManage: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [manager, setManager] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: allGroups = [], isLoading } = useAgreementGroups(canManage && editing);
  const setGroups = useSetAgreementGroups(agreementId);
  const current = new Set(groups.map((g) => g.id));

  const toggle = (id: string) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setError(null);
    setGroups.mutate([...next], {
      onError: (e) => setError(groupErrorMessage(e, "Nie udało się zmienić grup umowy.")),
    });
  };

  return (
    <div className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
          <Layers size={15} className="text-gray-400 dark:text-gray-500" />
          Grupy umowy
        </h3>
        {canManage && (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="secondary" onClick={() => setManager(true)} title="Zakładanie, zmiana nazwy i usuwanie grup">
              <Settings2 size={13} /> Grupy
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing((e) => !e)}>
              <Pencil size={13} /> {editing ? "Zakończ" : "Przypisz"}
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {groups.length === 0 ? (
          <span title="Umowa nie jest w żadnej grupie — w wycenie kosztorysu pokaże się pod „Bez przypisanej grupy”.">
            <Badge color="gray">{NO_GROUP_LABEL}</Badge>
          </span>
        ) : (
          groups.map((g) => (
            <Badge key={g.id} color="blue">
              {g.name}
            </Badge>
          ))
        )}
      </div>

      {error && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {canManage && editing && (
        <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
          {isLoading ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">Wczytywanie grup…</p>
          ) : allGroups.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Nie ma jeszcze żadnej grupy — załóż pierwszą przyciskiem „Grupy”.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
              {allGroups.map((g) => (
                <label
                  key={g.id}
                  className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={current.has(g.id)}
                    disabled={setGroups.isPending}
                    onChange={() => toggle(g.id)}
                  />
                  <span className="truncate">{g.name}</span>
                  {g.description && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
                      {g.description}
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      <AgreementGroupsManagerModal open={manager} onClose={() => setManager(false)} />
    </div>
  );
}
